import { gql } from '@apollo/client';
import { MISSION_FRAGMENT } from '../liveops/components/apolloQueries';
import { HURDLE_QUIZ_FRAGMENT } from '../liveops/screens/HurdleQuiz/graphql';

export const GET_SESSIONS = gql`
  query getLogs($userID: ID, $installID: ID, $startDate: Date!) {
    analytics {
      id
      appSessions(userId: $userID, installId: $installID, startDate: $startDate) {
        data
      }
    }
  }
`;

export const GET_SESSION_LOGS = gql`
  query getSessionLogs($sessionID: ID!, $sessionDate: Date!) {
    analytics {
      id
      appLogsBySession(sessionId: $sessionID, sessionDate: $sessionDate) {
        frontendLogs {
          data
          error
        }
        backendLogs {
          data
          error
        }
        crashLogs {
          data
          error
        }
      }
    }
  }
`;

export const GET_RUNTIME_LOGS = gql`
  query getRuntimeLogs($runtimeID: ID!, $offset: Int!, $limit: Int!, $startDate: Date) {
    analytics {
      id
      backendLogsByRuntime(runtimeId: $runtimeID, offset: $offset, limit: $limit, startDate: $startDate) {
        data
      }
    }
  }
`;

export const GET_USER_FROM_SESSION = gql`
  query getUser($property: String!, $type: UserSearchType!) {
    user(property: $property, type: $type) {
      id
      displayName
      picture
      subscriptionTier
      phoneCredential
      emailCredential
      facebookCredential
      appleCredential
      googleCredential
      userLevel
      userLevelProgressToNextLevel
      coinBalance
      diamondBalance
      energySystems {
        tournamentTicket {
          extraBalance
        }
      }
      basicTierContentPack {
        contentPack {
          name
        }
      }
      ABTestList {
        id
        variation
      }
      stampCards
      totalStampCardsEarned
    }
  }
`;

export const GET_USERS_FROM_SESSION = gql`
  query getUsersFromSession($properties: [String!]!, $type: UserSearchType!) {
    users(properties: $properties, type: $type) {
      id
      displayName
      picture
      subscriptionTier
      phoneCredential
      emailCredential
      facebookCredential
      appleCredential
      googleCredential
      userLevel
      userLevelProgressToNextLevel
      coinBalance
      diamondBalance
      basicTierContentPack {
        contentPack {
          name
        }
      }
      ABTestList {
        id
        variation
      }
    }
  }
`;

export const BAN_USER_MUTATION = gql`
  mutation banUser($input: BanUserInput!) {
    banUser(input: $input) {
      user {
        id
        displayName
        picture
        role
      }
    }
  }
`;

export const UNBAN_USER_MUTATION = gql`
  mutation unbanUser($input: UnbanUserInput!) {
    unbanUser(input: $input) {
      user {
        id
        role
      }
    }
  }
`;

export const FORCE_USER_ABTEST_MUTATION = gql`
  mutation forceUserTest($input: ForceUserABTestInput!) {
    forceUserABTest(input: $input) {
      user {
        id
        ABTestList {
          id
          variation
        }
        ABTestForcedList {
          id
        }
      }
    }
  }
`;

const LOYALTY_FRAGMENT = gql`
  fragment LoyaltyFragment on Loyalty {
    futureCard {
      ...LoyaltyCardFragment
    }
    currentCard {
      ...LoyaltyCardFragment
    }
    previousCard {
      ...LoyaltyCardFragment
    }
    tradeableCardCount
    cardStreak
    dayStreak
    previousCardStreak
  }

  fragment LoyaltyCardFragment on LoyaltyCard {
    id
    isExpired
    isCompleted
    currentDayIndex
    nextClaimableDayIndex
    days {
      rewards {
        __typename
        ... on Currency {
          amount
        }
        ... on CurrencyBoost {
          boostType
        }
        ... on CurrencyUnlimitedBoost {
          unlimitedBoostType
        }
      }
      isClaimed
      claimedMultiplier
      claimedDate
      start
      end
    }
  }
`;

const MISSION_PROGRESS_FRAGMENT = gql`
  fragment MissionProgressFragment on MissionProgress {
    id
    mission {
      ...MissionFragment
    }
    progress
    progressGoal
    completedDate
    claimedDate
    claimedMultiplier
  }
  ${MISSION_FRAGMENT}
`;

const MONTHLY_PASS_MISSIONS_FRAGMENT = gql`
  fragment MonthlyPassMissionsFragment on UserMonthlyPassMissions {
    id
    dailyMissions {
      ...MissionProgressFragment
    }
    monthlyMissions {
      ...MissionProgressFragment
    }
    extraMission {
      ...MissionProgressFragment
    }
    extraMissionNo
  }
  ${MISSION_PROGRESS_FRAGMENT}
`;

const USER_FRAGMENT = gql`
  fragment UserViewerFragment on User {
    id
    displayName
    picture
    lastOnline
    emailCredential
    subscriptionTier
    subscriptionDetails {
      sku
      autoRenews
      activeSince
      expires
      isFreeTrial
    }
    userLevel
    userLevelProgressToNextLevel
    currentUserMonthlyPass {
      progressToNextLevel
      level
      missions {
        ...MonthlyPassMissionsFragment
      }
    }
    achievementMissions {
      id
      missions {
        ...MissionProgressFragment
      }
    }
    coinBalance
    diamondBalance
    claimedUpdateGiftId
    vipDays
    premiumDays
    energySystems {
      tournamentTicket {
        currentCapSupply
        extraBalance
      }
      coin {
        currentCapSupply
      }
      xp {
        currentCapSupply
      }
      liveEventTicket {
        currentCapSupply
      }
    }
    liveEventCurrency
    boostRemove2Balance
    boostTeamSpiritBalance
    boostReviveBalance
    boostNinjaBalance
    boostBombBalance
    boostSpeedsterBalance
    boostArtistOnlyBalance
    boostHurdleHintBalance
    stampCards
    totalStampCardsEarned
    basicTierContentPack {
      id
      contentPack {
        id
        name
        picture
      }
      level
      progressToNextLevel
      totalXP
      currentMonthXP
      accuracy
      highestScore
      averageScore
      roundPlayedCount
      contentDiscoveredCount
      totalContentItemsCount
    }
    fragmentBalance {
      id
      category {
        id
        name
      }
      amount
    }
    stickers {
      id
    }
    profileFrames {
      id
    }
    appSkins {
      id
    }
    ABTestList {
      id
      variation
    }
    ABTestForcedList {
      id
    }
    ABTestConfigs {
      id
      variations
      status
    }
    ABTestLastAppInstallList {
      id
      variation
    }
    role
    reports {
      reporter {
        id
      }
      created
      comment
    }
    unlimitedBoosts {
      unlimitedBoostRemove2Balance
      unlimitedBoostSpeedsterBalance
    }
    loyalty {
      ...LoyaltyFragment
    }
    ownedContentPacks(limit: 100) {
      list {
        contentPack {
          id
          popularity
          explicitContentStatus
          name
        }
      }
    }
    savedContentPacks(limit: 100) {
      list {
        id
        level
        progressToNextLevel
        totalXP
        currentMonthXP
        accuracy
        highestScore
        averageScore
        roundPlayedCount
        contentDiscoveredCount
        totalContentItemsCount
        contentPack {
          id
          name
          picture
        }
      }
    }
    recentContentPacks(limit: 5) {
      list {
        id
        level
        progressToNextLevel
        totalXP
        currentMonthXP
        accuracy
        highestScore
        averageScore
        roundPlayedCount
        contentDiscoveredCount
        totalContentItemsCount
        contentPack {
          id
          name
          picture
        }
      }
    }
    currentSpecialEvent {
      id
      meScoreDetails {
        type
        score
      }
    }
    piggyBank {
      coins
      diamonds
    }
    metaFragments {
      BRONZE {
        amount
      }
      SILVER {
        amount
      }
      GOLD {
        amount
      }
    }
    shards {
      BRONZE
      SILVER
      GOLD
    }
    accoladeBalance
    team {
      id
    }
    freeSpins
    calendars {
      id
      name
    }
  }

  ${LOYALTY_FRAGMENT}
  ${MONTHLY_PASS_MISSIONS_FRAGMENT}
  ${MISSION_FRAGMENT}
`;

export const RESET_USER_SUBSCRIPTIONS_MUTATION = gql`
  mutation resetUserSubscriptions($input: ResetUserSubscriptionsInput!) {
    resetUserSubscriptions(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const SET_SUBSCRIPTION_EXPIRES_MUTATION = gql`
  mutation setSubscriptionExpires($input: SetSubscriptionExpiresInput!) {
    setSubscriptionExpires(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const SEARCH_USER = gql`
  query searchUser($input: String!, $type: UserSearchType!) {
    user(property: $input, type: $type) {
      ...UserViewerFragment
      isInfluencer
      influencerId
      testCredential
    }
  }
  ${USER_FRAGMENT}
`;

export const UPDATE_USER = gql`
  mutation updateUser($input: ForceUpdateUserInput!) {
    forceUpdateUser(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

const USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD = gql`
  fragment UserPlaylistProgressAdminPayload on UserContentPack {
    id
    level
    progressToNextLevel
    totalXP
    currentMonthXP
    accuracy
    highestScore
    averageScore
    roundPlayedCount
    contentDiscoveredCount
    totalContentItemsCount
    contentPack {
      id
      name
      picture
    }
  }
`;

export const USER_PLAYLIST_PROGRESS_ADMIN = gql`
  query userPlaylistProgressAdmin($input: UserPlaylistProgressLookupAdminInput!) {
    userPlaylistProgressAdmin(input: $input) {
      ...UserPlaylistProgressAdminPayload
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const SAVE_USER_PLAYLIST_PROGRESS_ADMIN = gql`
  mutation saveUserPlaylistProgressAdmin($input: UserPlaylistProgressAdminInput!) {
    saveUserPlaylistProgressAdmin(input: $input) {
      userContentPack {
        ...UserPlaylistProgressAdminPayload
      }
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const SET_USER_CURRENT_BASIC_CONTENT_PACK_ADMIN = gql`
  mutation setUserCurrentBasicContentPackAdmin($input: SetUserCurrentBasicContentPackAdminInput!) {
    setUserCurrentBasicContentPackAdmin(input: $input) {
      userContentPack {
        ...UserPlaylistProgressAdminPayload
      }
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const UPDATE_USER_PLAYLIST_PROGRESS_ADMIN = gql`
  mutation updateUserPlaylistProgressAdmin($input: UserPlaylistProgressAdminInput!) {
    updateUserPlaylistProgressAdmin(input: $input) {
      userContentPack {
        ...UserPlaylistProgressAdminPayload
      }
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const UNSAVE_USER_PLAYLIST_ADMIN = gql`
  mutation unsaveUserPlaylistAdmin($input: UnsaveUserPlaylistAdminInput!) {
    unsaveUserPlaylistAdmin(input: $input) {
      userContentPack {
        ...UserPlaylistProgressAdminPayload
      }
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const FLUSH_USER_CACHE_MUTATION = gql`
  mutation refreshUserRedisCache($input: RefreshUserRedisCacheInput!) {
    refreshUserRedisCache(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const FLUSH_REDIS_CACHE_FOR_ONE_USER_MUTATION = gql`
  mutation flushRedisCacheForOneUser($input: FlushRedisCacheForOneUserInput!) {
    flushRedisCacheForOneUser(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const GET_USER_LAST_SESSION = gql`
  query getUserLastSession($id: ID!) {
    analytics {
      id
      userLastSession(id: $id) {
        id
        date
      }
    }
  }
`;

export const USER_GIFT_STATUS_ADMIN = gql`
  query userGiftStatusAdmin($userId: ID!) {
    userGiftStatusAdmin(userId: $userId) {
      isFriend
      isSystem
      sender {
        __typename
        ... on User {
          id
          displayName
        }
        ... on Agent {
          id
          displayName
        }
      }
      gift {
        id
        created
        claimed
        bundleName
        message
        rewards {
          __typename
          ... on Currency {
            amount
          }
          ... on CurrencyBoost {
            boostType
          }
          ... on CurrencyUnlimitedBoost {
            unlimitedBoostType
          }
          ... on CurrencyCategoryFragment {
            category {
              id
              name
            }
          }
          ... on CurrencyCategoryRandom {
            category {
              id
              name
            }
          }
          ... on AppSkin {
            id
            name
          }
          ... on Badge {
            id
            name
            badgeType: type
          }
          ... on ContentPackReward {
            id
            rewardType: type
            contentPack {
              id
              name
            }
          }
          ... on Emoji {
            id
            name
          }
          ... on EmojiPack {
            id
            name
          }
          ... on Poster {
            id
            name
            posterType: type
          }
          ... on ProfileFrame {
            id
            name
          }
          ... on Sticker {
            id
            name
          }
        }
      }
    }
  }
`;

export const USER_BADGES_ADMIN = gql`
  query userBadgesAdmin($userId: ID!) {
    userBadgesAdmin(userId: $userId) {
      id
      name
      type
      owned
      isSP2
      currentLevel
      details
      createdAt
      updatedAt
    }
  }
`;

export const GRANT_USER_BADGE_ADMIN = gql`
  mutation grantUserBadgeAdmin($input: GrantUserBadgeAdminInput!) {
    grantUserBadgeAdmin(input: $input) {
      badge {
        id
        name
        type
        currentLevel
      }
    }
  }
`;

export const REVOKE_USER_BADGE_ADMIN = gql`
  mutation revokeUserBadgeAdmin($input: RevokeUserBadgeAdminInput!) {
    revokeUserBadgeAdmin(input: $input) {
      userId
      badgeId
    }
  }
`;

export const SET_USER_BADGE_LEVEL_ADMIN = gql`
  mutation setUserBadgeLevelAdmin($input: SetUserBadgeLevelAdminInput!) {
    setUserBadgeLevelAdmin(input: $input) {
      badge {
        id
        name
        type
        currentLevel
      }
    }
  }
`;

export const APP_INFORMATION = gql`
  query appInformation {
    me {
      energySystems {
        coin {
          caps {
            basic {
              maxStorage
            }
            ad_free {
              maxStorage
            }
            premium {
              maxStorage
            }
            vip {
              maxStorage
            }
            plus {
              maxStorage
            }
          }
        }
        xp {
          caps {
            basic {
              maxStorage
            }
            ad_free {
              maxStorage
            }
            premium {
              maxStorage
            }
            vip {
              maxStorage
            }
            plus {
              maxStorage
            }
          }
        }
        tournamentTicket {
          caps {
            basic {
              maxStorage
            }
            ad_free {
              maxStorage
            }
            premium {
              maxStorage
            }
            vip {
              maxStorage
            }
            plus {
              maxStorage
            }
          }
        }
        liveEventTicket {
          caps {
            basic {
              maxStorage
            }
            ad_free {
              maxStorage
            }
            premium {
              maxStorage
            }
            vip {
              maxStorage
            }
            plus {
              maxStorage
            }
          }
        }
      }
    }
    app {
      contentCategories {
        id
        name
        icon
      }
    }
  }
`;

export const LIVE_OPS_CONTENT = gql`
  query liveOpsContent {
    liveOps {
      stickers {
        list {
          id
          name
          asset
          liveOpsAvailability
          nextLevelItems {
            level
          }
        }
      }
      appskins {
        list {
          id
          name
          iconAsset
          liveOpsAvailability
          nextLevelItems {
            level
          }
        }
      }
      profileFrames {
        list {
          id
          name
          asset
          liveOpsAvailability
          nextLevelItems {
            level
            asset
          }
        }
      }
    }
  }
`;

export const UPDATE_LOYALTY_CARD = gql`
  mutation updateLoyaltyCard($input: UpdateLoyaltyCardInput!) {
    updateLoyaltyCard(input: $input) {
      user {
        id
        loyalty {
          ...LoyaltyFragment
        }
      }
    }
  }
  ${LOYALTY_FRAGMENT}
`;

export const RESET_LOYALTY_CARD = gql`
  mutation resetLoyaltyCard($input: ResetLoyaltyCardInput!) {
    resetLoyaltyCard(input: $input) {
      user {
        id
        loyalty {
          ...LoyaltyFragment
        }
      }
    }
  }
  ${LOYALTY_FRAGMENT}
`;

export const REVIVE_LOYALTY_CARD = gql`
  mutation reviveLoyaltyCard($input: ReviveLoyaltyCardInput!) {
    reviveLoyaltyCard(input: $input) {
      user {
        id
        loyalty {
          ...LoyaltyFragment
        }
      }
    }
  }
  ${LOYALTY_FRAGMENT}
`;

export const SHIFT_LOYALTY_CARD = gql`
  mutation shiftLoyaltyCard($input: ShiftLoyaltyCardInput!) {
    shiftLoyaltyCard(input: $input) {
      user {
        id
        loyalty {
          ...LoyaltyFragment
        }
      }
    }
  }
  ${LOYALTY_FRAGMENT}
`;

export const UPDATE_USER_SPECIAL_EVENT_POINTS = gql`
  mutation updateUserSpecialEventPoints($input: UpdateUserSpecialEventPointsInput!) {
    updateUserSpecialEventPoints(input: $input) {
      user {
        id
        currentSpecialEvent {
          meScoreDetails {
            type
            score
          }
        }
      }
    }
  }
`;

export const UPDATE_USER_INFLUENCER = gql`
  mutation updateUserInfluencer($input: UpdateUserInfluencerInput!) {
    updateUserInfluencer(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const GET_USER_LOYALTY_CAMPAIGN_PROGRESS = gql`
  query getUserLoyaltyCampaignProgress($userId: String!) {
    user(property: $userId, type: BY_ID) {
      id
      loyaltyCampaignProgress {
        loyaltyPoints
        claimedLoyaltyCards
      }
      loyaltyCampaignRewardLogs {
        referenceId
        sourceType
        productId
        amount
        created
      }
    }
    liveOps {
      id
      loyaltyCampaignCards {
        id
        name
      }
    }
  }
`;

export const SET_USER_LOYALTY_CAMPAIGN_PROGRESS = gql`
  mutation setUserLoyaltyCampaignProgress($userId: ID!, $loyaltyPoints: Int!, $claimedLoyaltyCardIds: [ID!]!) {
    setUserLoyaltyCampaignProgress(
      userId: $userId
      loyaltyPoints: $loyaltyPoints
      claimedLoyaltyCardIds: $claimedLoyaltyCardIds
    ) {
      id
      loyaltyCampaignProgress {
        loyaltyPoints
        claimedLoyaltyCards
      }
    }
  }
`;

export const RESET_USER_LOYALTY_CAMPAIGN_PROGRESS = gql`
  mutation resetUserLoyaltyCampaignProgress($userId: ID!) {
    resetUserLoyaltyCampaignProgress(userId: $userId) {
      id
      loyaltyCampaignProgress {
        loyaltyPoints
        claimedLoyaltyCards
      }
    }
  }
`;

export const SEND_RETENTION_REMINDER = gql`
  mutation sendRetentionReminder($input: SendRetentionReminderInput) {
    sendRetentionReminder(input: $input)
  }
`;

export const SEND_NOTIFICATION = gql`
  mutation sendNotification($input: SendNotificationInput!) {
    sendNotification(input: $input)
  }
`;

export const USER_TRANSACTIONS = gql`
  query userTransactions($userId: ID!, $startDate: String!, $endDate: String!) {
    analytics {
      userTransactions(userId: $userId, startDate: $startDate, endDate: $endDate)
    }
  }
`;

export const USER_TOTAL_SPEND = gql`
  query userTotalSpend($userId: ID!) {
    analytics {
      userTotalSpend(userId: $userId)
    }
  }
`;

export const GET_MONTH_ID_LIST = gql`
  query getUserMonthIdList($input: GetUserMonthIdListInput!) {
    app {
      getUserMonthIdList(input: $input) {
        list
      }
    }
  }
`;

export const SHOW_MISSION_PROGRESS = gql`
  query getUserMonthlyPass($input: GetUserMonthlyPassInput!) {
    app {
      getUserMonthlyPass(input: $input) {
        userMonthlyPass {
          allMissions {
            id
            dailyMissions {
              id
              mission {
                id
                start
                end
                requiredTier
                tasks {
                  type
                  params {
                    __typename
                  }
                }
                rewardXp
              }
              progress
              progressGoal
              completedDate
              claimedDate
              claimedMultiplier
            }
            monthlyMissions {
              id
              mission {
                id
                start
                end
                requiredTier
                tasks {
                  type
                  params {
                    __typename
                  }
                }
                rewardXp
              }
              progress
              progressGoal
              completedDate
              claimedDate
              claimedMultiplier
            }
            extraMissions {
              id
              mission {
                id
                start
                end
                requiredTier
                tasks {
                  type
                  params {
                    __typename
                  }
                }
                rewardXp
              }
              progress
              progressGoal
              completedDate
              claimedDate
              claimedMultiplier
            }
            teamMissions {
              id
              mission {
                id
                start
                end
                requiredTier
                tasks {
                  type
                  params {
                    __typename
                  }
                }
                rewardXp
              }
              progress
              progressGoal
              completedDate
              claimedDate
              claimedMultiplier
            }
          }
        }
      }
    }
  }
`;

export const RESET_MISSION_PROGRESS = gql`
  mutation resetMissionProgress($input: ResetMissionProgressInput!) {
    resetMissionProgress(input: $input) {
      user {
        currentUserMonthlyPass {
          missions {
            ...MonthlyPassMissionsFragment
          }
        }
      }
    }
  }
  ${MONTHLY_PASS_MISSIONS_FRAGMENT}
`;

export const FORCE_REWARDS_FOR_MP_LEADERBOARD = gql`
  mutation forceGrantMPLeaderboardRewards($input: ForceGrantMPLRewardsInput!) {
    forceGrantMPLeaderboardRewards(input: $input)
  }
`;

export const FORCE_MISSION_PROGRESS = gql`
  mutation forceMissionProgress($input: ForceMissionProgressInput!) {
    forceMissionProgress(input: $input) {
      missionProgress {
        id
        progress
        progressGoal
        completedDate
        claimedDate
      }
    }
  }
`;

// Same BE mutation as RESET_MISSION_PROGRESS, but selects achievement missions in
// the response so Apollo refreshes the achievement display after reset.
export const RESET_ACHIEVEMENT_PROGRESS = gql`
  mutation resetAchievementProgress($input: ResetMissionProgressInput!) {
    resetMissionProgress(input: $input) {
      user {
        id
        achievementMissions {
          id
          missions {
            ...MissionProgressFragment
          }
        }
      }
    }
  }
  ${MISSION_PROGRESS_FRAGMENT}
`;

export const GET_SP2_LINK_STATE = gql`
  query getLinkStateAdmin($input: Sp3IdInput!) {
    getLinkStateAdmin(input: $input) {
      state
      sp2id
    }
  }
`;

export const GET_SP2_LINK_STATE_ON_SP2 = gql`
  query getLinkStateBySp3IdFromSp2($input: Sp3IdInput!) {
    getLinkStateBySp3IdFromSp2(input: $input) {
      state
      sp2id
    }
  }
`;

export const LINK_TO_SP2_ID = gql`
  mutation linkToSp2Admin($input: Sp2LinkIdPairInput!) {
    linkToSp2Admin(input: $input) {
      error
      errorMessage
      success
      isPending
    }
  }
`;
export const UNLINK_ID_PAIR = gql`
  mutation unlinkIdPair($input: Sp2LinkIdPairInput!) {
    unlinkIdPair(input: $input) {
      error
      errorMessage
      success
      isPending
    }
  }
`;

export const GET_CURRENT_HURDLE_QUIZZES = gql`
  query currentHurdleQuizzes($input: CurrentHurdleQuizzesInput!) {
    currentHurdleQuizzes(input: $input) {
      id
      hasMore
      totalCount
      list {
        ...HurdleQuizFragment
      }
    }
  }
  ${HURDLE_QUIZ_FRAGMENT}
`;

export const RESET_USER_HURDLE_QUIZ = gql`
  mutation resetHurdleQuiz($input: ResetHurdleQuizInput!) {
    resetHurdleQuiz(input: $input) {
      hurdleQuiz {
        ...HurdleQuizFragment
      }
    }
  }
  ${HURDLE_QUIZ_FRAGMENT}
`;

export const UPDATE_USER_HURDLE_QUIZ = gql`
  mutation updateUserHurdleQuiz($input: UpdateUserHurdleQuizInput!) {
    updateUserHurdleQuiz(input: $input) {
      hurdleQuiz {
        ...HurdleQuizFragment
      }
    }
  }
  ${HURDLE_QUIZ_FRAGMENT}
`;

export const COMPLETE_USER_HURDLE_QUIZ = gql`
  mutation completeUserHurdleQuiz($input: CompleteUserHurdleQuizInput!) {
    completeUserHurdleQuiz(input: $input) {
      hurdleQuiz {
        ...HurdleQuizFragment
      }
    }
  }
  ${HURDLE_QUIZ_FRAGMENT}
`;

export const GET_USER_STREAK_HISTORY = gql`
  query streakHistory($input: StreakHistoryInput!) {
    streakHistory(input: $input) {
      currentStreak
      lostGames {
        hurdleQuizId
        previousStreak
        lostDate
        songTitle
        songArtist
      }
    }
  }
`;

export const GET_ACTIVE_USER_SUBSCRIPTION_UPDATE_LOGS = gql`
  query activeUserSubscriptionUpdateLogs($input: ActiveUserSubscriptionUpdateLogsInput!) {
    activeUserSubscriptionUpdateLogs(input: $input) {
      logs {
        userId
        logId
        store
        type
        sku
        environment
        originalTransactionId
        created
        expires
        purchased
        cancelled
        cancelledReason
        autoRenew
        retrying
        valid
        iosWebOrderLineItemId
      }
    }
  }
`;

export const APPLY_USER_SUBSCRIPTION_UPDATE_LOG = gql`
  mutation applyUserSubscriptionUpdateLog($input: ApplyUserSubscriptionUpdateLogInput!) {
    applyUserSubscriptionUpdateLog(input: $input) {
      user {
        ...UserViewerFragment
        isInfluencer
        influencerId
        testCredential
      }
    }
  }
  ${USER_FRAGMENT}
`;

const QUIZ_LIVE_SHOW_USER_FRAGMENT = gql`
  fragment QuizLiveShowUserFragment on QuizLiveShowUser {
    id
    diamondReward
    ghostQuestionId
    reviveQuestionId
    show {
      id
      startDate
      lastQuestionStartDate
      diamondPrize
      rounds {
        id
        questionDuration
        questions {
          id
        }
      }
      winners {
        id
        user {
          id
        }
      }
    }
  }
`;

export const GET_QUIZ_LIVE_SHOW_USER = gql`
  query quizLiveShowUser($userId: ID!, $playDate: Date!) {
    quizLiveShowUser(userId: $userId, playDate: $playDate) {
      ...QuizLiveShowUserFragment
    }
  }
  ${QUIZ_LIVE_SHOW_USER_FRAGMENT}
`;

export const REFUND_LIVE_SHOW = gql`
  mutation refundLiveShow($userId: ID!, $showId: ID!) {
    refundLiveShowUserBoosts(input: { userId: $userId, showId: $showId }) {
      user {
        id
      }
    }
  }
`;

// Year In Review section
export const YEAR_IN_REVIEW_FRAGMENT = gql`
  fragment YearInReviewFragment on YearInReview {
    id
    year
    seen
    available
  }
`;

export const GET_ALL_YEAR_IN_REVIEW = gql`
  query getAllYearInReview($userId: String!, $type: UserSearchType!) {
    user(property: $userId, type: $type) {
      getAllYearInReview {
        ...YearInReviewFragment
      }
    }
  }
  ${YEAR_IN_REVIEW_FRAGMENT}
`;

export const CHANGE_AVAILABLE_DATE_YIR = gql`
  mutation changeAvailableDateYIR($userId: ID!, $year: Int!, $date: Date!) {
    changeAvailableDateYearInReview(input: { userId: $userId, year: $year, date: $date }) {
      ...YearInReviewFragment
    }
  }
  ${YEAR_IN_REVIEW_FRAGMENT}
`;

export const GET_TEAM_HEAT_INFO = gql`
  query getTeamHeatInfo($property: String!, $type: UserSearchType!) {
    user(property: $property, type: $type) {
      id
      team {
        id
        availableBoosts
        boosts {
          type
          expireTimestamp
        }
        teamHeat {
          id
          currentScore
          lastUpdateScore
          lastUpdateScoreTimestamp
          maxHeatExpireTimestamp
          isMaxHeat
        }
        teamMonthlyPass {
          id
          teamHeatIteration
          availableTeamHeatMilestone
        }
      }
      currentUserMonthlyPass {
        id
        claimedTeamHeatMilestone
      }
    }
  }
`;

export const UPDATE_TEAM_HEAT_INFO = gql`
  mutation updateTeamHeatInfo($input: TeamHeatUpdateInput!) {
    updateTeamHeat(input: $input) {
      teamHeat {
        id
        currentScore
        lastUpdateScore
        lastUpdateScoreTimestamp
        maxHeatExpireTimestamp
        isMaxHeat
      }
      teamMonthlyPass {
        id
        teamHeatIteration
        availableTeamHeatMilestone
      }
    }
  }
`;

export const UPDATE_TEAM_BOOSTS = gql`
  mutation updateTeamBoosts($input: UpdateTeamBoostsInput!) {
    updateTeamBoosts(input: $input) {
      team {
        id
        availableBoosts
        boosts {
          type
          expireTimestamp
        }
      }
    }
  }
`;

export const RESET_CLAIMED_TEAM_HEAT_MILESTONES = gql`
  mutation resetClaimedTeamHeatMilestones($input: ResetTeamHeatClaimedMilestonesInput!) {
    resetClaimedTeamHeatMilestones(input: $input) {
      userMonthlyPass {
        id
        claimedTeamHeatMilestone
      }
    }
  }
`;

export const USER_EMOJI_PACK_FRAGMENT = gql`
  fragment UserEmojiPackFragment on UserEmojiPackPaginated {
    id
    totalCount
    hasMore
    list {
      isActive
      availability {
        isAvailable
        type
      }
      emojiPack {
        id
        name
        asset
        layout
        emojis {
          id
          asset
          name
          utfCode
        }
      }
    }
  }
`;

export const USER_EMOJI_PACKS = gql`
  query userEmojiPacks($property: String!, $type: UserSearchType!, $limit: Int, $offset: Int) {
    user(property: $property, type: $type) {
      id
      emojiPacks(limit: $limit, offset: $offset) {
        ...UserEmojiPackFragment
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const DELETE_USER_EMOJI_PACK = gql`
  mutation deleteUserEmojiPack($input: DeleteUserEmojiPackInput!) {
    deleteUserEmojiPack(input: $input) {
      user {
        id
        emojiPacks {
          ...UserEmojiPackFragment
        }
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const UPDATE_USER_EMOJI_PACK = gql`
  mutation updateUserEmojiPack($input: UpdateUserEmojiPackInput!) {
    updateUserEmojiPack(input: $input) {
      user {
        id
        emojiPacks {
          ...UserEmojiPackFragment
        }
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const ADD_USER_EMOJI_PACK = gql`
  mutation addUserEmojiPack($input: AddUserEmojiPackInput!) {
    addUserEmojiPack(input: $input) {
      user {
        id
        emojiPacks {
          ...UserEmojiPackFragment
        }
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const RESET_EMOJI_PACK_TUTOR = gql`
  mutation resetEmojiPackTutorial($userId: ID!) {
    resetEmojiPackTutorial(userId: $userId) {
      user {
        id
        emojiPacks {
          ...UserEmojiPackFragment
        }
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const RESET_USER_DAILY_EMOJIS = gql`
  mutation resetDailyEmojis($userId: ID!) {
    resetDailyEmojis(userId: $userId)
  }
`;

const USER_EXPORTS_FRAGMENT = gql`
  fragment UserExportsFragment on UserExport {
    id
    requested
    processed
    status
    jobStatus
    jobStartedAt
    exportedAt
    fileExpiresAt
  }
`;

export const GET_USER_EXPORTS = gql`
  query getUserExports($input: UserExportsInput!) {
    userExports(input: $input) {
      userExports {
        ...UserExportsFragment
      }
    }
  }
  ${USER_EXPORTS_FRAGMENT}
`;

export const GET_SIGNED_DOWNLOAD_URL = gql`
  query getSignedDownloadUrl($input: GetSignedDownloadUrlInput!) {
    getSignedDownloadUrl(input: $input) {
      url
    }
  }
`;

export const START_EXPORT_JOB = gql`
  mutation startExportJob($input: StartExportJobInput!) {
    startExportJob(input: $input) {
      userExport {
        ...UserExportsFragment
      }
    }
  }
  ${USER_EXPORTS_FRAGMENT}
`;

export const CREATE_EXPORT_REQUEST = gql`
  mutation createExportRequest($input: CreateExportRequestInput!) {
    createExportRequest(input: $input) {
      userExport {
        ...UserExportsFragment
      }
    }
  }
  ${USER_EXPORTS_FRAGMENT}
`;

export const PROCESS_EXPORT_REQUEST = gql`
  mutation processExportRequest($input: ProcessExportRequestInput!) {
    processExportRequest(input: $input) {
      userExport {
        ...UserExportsFragment
      }
    }
  }
  ${USER_EXPORTS_FRAGMENT}
`;

export const DELETE_USER_CALENDAR = gql`
  mutation deleteUserCalendar($calendarId: ID!, $userId: ID!) {
    deleteUserCalendar(calendarId: $calendarId, userId: $userId) {
      id
      calendars {
        id
        name
      }
    }
  }
`;

export const GET_CURRENT_USER_MARATHON_EVENT = gql`
  query getCurrentUserMarathonEvent($property: String!, $type: UserSearchType!) {
    user(property: $property, type: $type) {
      id
      userMarathonEvent {
        id
        eventId
        checkPoint
        currentRound {
          id
          state
        }
        maxSongReached
        milestoneClaimed {
          index
          isBasicClaimed
          isPlusClaimed
        }
      }
    }
  }
`;

export const UPDATE_USER_MARATHON_EVENT = gql`
  mutation updateUserMarathonEvent($input: UpdateUserMarathonEventInput!) {
    updateUserMarathonEvent(input: $input) {
      userEvent {
        id
        eventId
        checkPoint
        currentRound {
          id
          state
        }
        maxSongReached
        milestoneClaimed {
          index
          isBasicClaimed
          isPlusClaimed
        }
      }
    }
  }
`;

export const RESET_USER_MARATHON_EVENT = gql`
  mutation resetUserMarathonEvent($input: ResetUserMarathonEventInput!) {
    resetUserMarathonEvent(input: $input) {
      userEvent {
        id
        eventId
        checkPoint
        currentRound {
          id
          state
        }
        maxSongReached
        milestoneClaimed {
          index
          isBasicClaimed
          isPlusClaimed
        }
      }
    }
  }
`;

export const GET_USER_MARATHON_ACHIEVEMENTS = gql`
  query getUserMarathonAchievements($property: String!, $type: UserSearchType!) {
    user(property: $property, type: $type) {
      id
      achievementMissions {
        id
        missions {
          ...MissionProgressFragment
        }
      }
    }
  }
  ${MISSION_PROGRESS_FRAGMENT}
`;

export const UPDATE_USER_MARATHON_ACHIEVEMENTS = gql`
  mutation updateUserMarathonAchievementProgress($input: UpdateUMEAchievement!) {
    updateUserMarathonAchievementProgress(input: $input)
  }
`;
